import { useState } from 'react';
import { formatNumber } from 'core/utils/numberFormat';
import type { SupportedLocale } from 'core/utils/numberFormat';
import { parseLocalizedNumber } from 'core/utils/parseLocalizedNumber';

export const FORMAT_OPTIONS: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
export const EMPTY_VALUE_INDICATOR = '-';
export const NUMERIC_INPUT_REGEX = /^[\d,.]*$/;

export function formatDisplayValue(
  val: number | string | null | undefined,
  locale: SupportedLocale,
  formatOptions: Intl.NumberFormatOptions = FORMAT_OPTIONS
): string {
  if (val == null || val === EMPTY_VALUE_INDICATOR) return EMPTY_VALUE_INDICATOR;
  const num = Number(val);
  if (!Number.isFinite(num)) return EMPTY_VALUE_INDICATOR;
  return formatNumber(num, locale, formatOptions);
}

type UseLocalizedInputParams = {
  initialValue: string;
  initialHasError?: boolean;
  locale: SupportedLocale;
  max: number | undefined;
  onChange?: ((value: number | null) => void) | null;
  transformBeforeEmit?: (parsed: number) => number;
  extraValidation?: (parsed: number) => boolean;
  formatOptions?: Intl.NumberFormatOptions;
};

export function useLocalizedInput({
  initialValue,
  initialHasError = false,
  locale,
  max,
  onChange,
  transformBeforeEmit,
  extraValidation,
  formatOptions = FORMAT_OPTIONS,
}: UseLocalizedInputParams) {
  const [value, setValue] = useState(initialValue);
  const [hasError, setHasError] = useState(initialHasError);

  const onChangeText = ({ target: { value: rawValue } }: { target: { value: string } }) => {
    const rawStr = String(rawValue);
    if (!NUMERIC_INPUT_REGEX.test(rawStr) && rawStr !== '') return;
    if (rawStr === '') {
      setValue(rawStr);
      setHasError(false);
      onChange?.(null);
      return;
    }
    const parsed = parseLocalizedNumber(rawStr, locale, 0, max);
    if (parsed === null) return;
    if (parsed < 0) {
      return;
    }
    const isOverMax = max !== undefined && parsed > max;
    if (isOverMax) {
      setValue(rawStr);
      setHasError(true);
      return;
    }
    if (!extraValidation || extraValidation(parsed)) {
      setValue(rawStr);
      setHasError(false);
      const emitValue = transformBeforeEmit ? transformBeforeEmit(parsed) : parsed;
      onChange?.(emitValue);
    } else {
      setValue(rawStr);
      setHasError(true);
    }
  };

  const onFocus = () => {
    if (value === EMPTY_VALUE_INDICATOR) setValue('');
  };

  const onBlur = () => {
    if (!value || value === EMPTY_VALUE_INDICATOR) {
      setValue(EMPTY_VALUE_INDICATOR);
      return;
    }
    const parsed = parseLocalizedNumber(value, locale, 0, max);
    if (parsed === null || !Number.isFinite(parsed)) {
      setValue(EMPTY_VALUE_INDICATOR);
      setHasError(false);
      onChange?.(null);
      return;
    }
    const isInRange = parsed >= 0 && (max === undefined || parsed <= max);
    const passesExtraValidation = !extraValidation || extraValidation(parsed);
    if (isInRange && passesExtraValidation) {
      setValue(formatNumber(parsed, locale, formatOptions));
    } else {
      // Keep the raw value and error state visible so the user can see and correct the issue
      setHasError(true);
    }
  };

  return { value, setValue, hasError, setHasError, onChangeText, onFocus, onBlur };
}
